// src/utils/lessonBuilderWriter.js
//
// Pass 70B — the ONE canonical, idempotent, identity-preserving writer for the Instructor Lesson Builder.
// It replaces InstructorLessonBuilderPage.persistLesson's direct setDoc/addDoc, which rebuilt identity/lineage
// from page state on every save and collapsed the two-ID model:
//     instructorId = currentUser.uid          // WRONG — Auth uid stamped into the business instructor id
//     assignedInstructorId = currentUser.uid
//     organizationId = userData.orgId          // WRONG — org rebuilt from page state, not the stored lesson
// For a booking-linked lesson (origin:'booking', instructorId = a business member id) that quietly corrupted
// the Pass-66 handoff identity. This writer enforces the delivery-phase invariants:
//   • EXISTING lesson → MERGE only coaching/delivery fields; ALL stored identity/lineage is preserved
//     (organizationId/origin/booking*/request*/clientId/instructorId/instructorAuthUserId/assigned*/participant).
//     The stored canonical lesson wins — never page state (currentUser.uid, userData.orgId, route/query).
//   • NEW standalone lesson → resolve the canonical BUSINESS instructorId from the caller's teaching membership
//     (never currentUser.uid), stamp the Auth uid separately in instructorAuthUserId, require an explicit org,
//     authorize against REAL membership (canCreateStandaloneLesson), and get-or-create on a caller-supplied
//     stable per-draft id so double-tap / retry = one record.

import { runTransaction, doc, serverTimestamp } from 'firebase/firestore';
import { createLessonInstancePayload, normalizeLessonInstanceRecord } from './lessonInstancePersistence';
import { canWriteQuickLog, canCreateStandaloneLesson } from './quickLessonLogWriter';

const LESSONS = 'lessonInstances';
const str = (v) => String(v == null ? '' : v).trim();
const fail = (code, msg) => { const e = new Error(msg); e.code = code; return e; };

export const LESSON_BUILDER_DENIED = 'You do not have permission to save this lesson.';
export const LESSON_BUILDER_ID_REQUIRED = 'A lesson id is required to save.';
export const LESSON_BUILDER_ORG_REQUIRED = 'A target organisation is required to create a lesson.';

// The ONLY fields the Lesson Builder may change on an EXISTING lesson: coaching / delivery content. Identity,
// lineage and participant fields are deliberately absent — they are preserved from the stored document.
function buildCoachingMerge(plan = {}, status) {
  const payload = createLessonInstancePayload(plan, { status });
  const merge = {
    sport: payload.sport,
    trackId: payload.trackId,
    targetSkillId: payload.targetSkillId,
    lessonFlowId: payload.lessonFlowId,
    recommendedBySystem: payload.recommendedBySystem,
    recommendationReason: payload.recommendationReason,
    stages: payload.stages,
    lessonOutcome: payload.lessonOutcome,
    status: payload.status,
    updatedAt: serverTimestamp(),
  };
  // videoId is coaching linkage (the reviewed video), additive-only: set it when provided, never blank a stored one.
  if (str(plan.videoId)) merge.videoId = str(plan.videoId);
  return merge;
}

// saveLessonBuilderLesson(db, { lessonInstanceId, lessonPlan, status, userData, actorAuthUserId,
//                               actorInstructorId, organizationId })
//   • lessonInstanceId REQUIRED and STABLE (the page generates one id per draft and reuses it) so every retry
//     lands on the same doc — idempotent get-or-create, no duplicate standalone lessons on double-save.
//   • existing doc → coaching-only merge (lineage/identity preserved, authorized as the assigned instructor or
//     an operator against the ALREADY-STORED instructorAuthUserId — never page state).
//   • missing doc → create ONE standalone lesson with the two-ID model resolved from real membership.
export async function saveLessonBuilderLesson(db, {
  lessonInstanceId = '',
  lessonPlan = {},
  status = 'draft',
  ...ctx
} = {}) {
  const id = str(lessonInstanceId);
  if (!id) throw fail('LESSON_BUILDER_ID_REQUIRED', LESSON_BUILDER_ID_REQUIRED);

  let created = false;
  await runTransaction(db, async (tx) => {
    const ref = doc(db, LESSONS, id);
    const snap = await tx.get(ref);
    const exists = snap.exists && snap.exists();

    if (exists) {
      // EXISTING lesson: the stored document is the canonical identity. Pass 84.2 — authorize via the CURRENT canonical
      // membership (member.userId == lesson.instructorId), NOT the stored instructorAuthUserId snapshot (relink-safe).
      const current = normalizeLessonInstanceRecord({ id, ...snap.data() }, id);
      if (!canWriteQuickLog(current, { ...ctx, organizationId: current.organizationId })) {
        throw fail('LESSON_BUILDER_DENIED', LESSON_BUILDER_DENIED);
      }
      // MERGE coaching fields ONLY. organizationId/origin/booking*/request*/clientId/instructorId/
      // instructorAuthUserId/assigned*/participant are NOT in the merge → preserved exactly as stored.
      tx.set(ref, buildCoachingMerge(lessonPlan, status), { merge: true });
      return;
    }

    // NEW standalone lesson (no booking). Resolve the two-ID model explicitly, never from currentUser.uid.
    const org = str(ctx.organizationId);
    if (!org) throw fail('LESSON_BUILDER_ORG_REQUIRED', LESSON_BUILDER_ORG_REQUIRED);
    if (!canCreateStandaloneLesson({ ...ctx, organizationId: org })) {
      throw fail('LESSON_BUILDER_DENIED', LESSON_BUILDER_DENIED);
    }
    const base = {
      ...lessonPlan,
      lessonId: id,
      organizationId: org,
      origin: 'manual',
      // canonical BUSINESS instructor id (a member id), resolved by the caller from membership — NOT the Auth uid.
      instructorId: str(ctx.actorInstructorId),
      assignedInstructorId: str(ctx.actorInstructorId),
      instructorAuthUserId: str(ctx.actorAuthUserId),   // the Auth uid, kept SEPARATE (two-ID model)
      assignmentSource: 'instructor_self',
      // participant identity is whatever the plan explicitly carried (a video-review student); never fabricated.
      clientId: str(lessonPlan.clientId),
    };
    const payload = createLessonInstancePayload(base, { status });
    tx.set(ref, {
      ...payload,
      createdBy: str(ctx.actorAuthUserId),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    created = true;
  });

  return { lessonInstanceId: id, created, status };
}
